import { Fragment, h } from 'preact'
import dayjs from 'dayjs'
import { useState, useEffect, useCallback } from 'preact/hooks'
import { useStateValue } from '../../state-management/store'
import { fetchAvailableTimes } from '../../state-management/actions'
import { BookingStep } from '../../models/enums'
import { ValueType } from '../../models/patient'
import { CustomField, CustomFieldType } from '../custom-field'
import {
    APPOINTMENT_TIME_FORMAT,
    APPOINTMENT_DATE_FORMAT,
    CALENDAR_DATE_FORMAT,
    BIG_SCREEN_WIDTH,
} from '../../shared/const'
import cx from '../../shared/classes'
import { useAsync } from '../../shared/custom-hooks/useAsync'
import removeDatetimeOffset from '../../shared/removeDatetimeOffset'
import { StepBody } from '../step-body'
import { SelfSchedulerFooter } from '../self-scheduler-footer'
import Calendar from './calendar'
import TimeTile from './time-tile'
import { ErrorMessage } from '../error-message'

import * as style from './style.css'

function timeStringToDateString(time: string): string {
    return time.substr(0, 10)
}
interface SeparatedTimes {
    morning: string[]
    afternoon: string[]
    evening: string[]
}

const minutesToSeconds = (minutes: number) => {
    return minutes * 60
}

const DateTimeSelector: preact.FunctionalComponent = () => {
    const [
        {
            locations,
            patientDetails,
            selectedLocationId,
            selectedAppointmentDate,
            selectedAppointmentAvailableTimes,
            selectedAppointmentTime: stateAppointmentTime,
            selectedAppointmentDateRangeFrom,
            selectedAppointmentDateRangeTo,
            availableTimes,
            treatmentLength,
            loader,
            showAdditionalDetailsForm,
            providers,
            customization,
        },
        dispatch,
    ] = useStateValue()

    const { run: runFetchAvailableTimes, status: fetchAvailableTimesStatus, data: fetchedAvailableTimes } = useAsync({
        status: 'pending',
    })

    const [nextAvailableAppointmentShown, setNextAvailableAppointmentShown] = useState<boolean>(false)
    const [preselectedFirstAvailableDate, setPreselectedFirstAvailableDate] = useState<boolean>(false)
    const [isFirstFetchAvailableTimes, setIsFirstFetchAvailableTimes] = useState<boolean>(true)
    const [noAvailableSlotsInCurrentMonth, setNoAvailableSlotsInCurrentMonth] = useState<boolean>(false)
    const [currentFrom, setCurrentFrom] = useState<string>('')
    const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>('')

    const selectedAppointmentTime = stateAppointmentTime && dayjs(stateAppointmentTime).format(APPOINTMENT_TIME_FORMAT)
    const selectedLocation = locations.find(({ id }) => id === selectedLocationId)
    const timeZone = selectedLocation?.timezone ?? 'America/New_York'
    const shortTimezone = new Date().toLocaleTimeString('en-us', { timeZone, timeZoneName: 'short' }).split(' ')[2]
    const daysWithAvailableAppointmentSlots = availableTimes.map((time) => {
        return timeStringToDateString(time)
    })
    const firstAvailableAppointmentTime = availableTimes && availableTimes[0]
    const procedureId = patientDetails.reasonOfAppointment?.value ? patientDetails.reasonOfAppointment.value : ''

    const enabledProviderSelection =
        customization?.scheduling_additional_features_and_fields.enabled_provider_selection ?? false

    const checkIfBigScreen = () => {
        return window.innerWidth > BIG_SCREEN_WIDTH
    }

    const handleSetSelectedAppointmentDate = useCallback(
        async (appointmentDate: string) => {
            setErrorMessage('')
            await dispatch({
                type: 'SET_SELECTED_APPOINTMENT_DATE',
                appointmentDate,
            })
        },
        [dispatch]
    )

    useEffect(() => {
        if (!preselectedFirstAvailableDate && fetchAvailableTimesStatus === 'resolved' && availableTimes[0]) {
            if (checkIfBigScreen() && !noAvailableSlotsInCurrentMonth) {
                handleSetSelectedAppointmentDate(timeStringToDateString(availableTimes[0]))
            }
            setPreselectedFirstAvailableDate(true)
        }
    }, [
        availableTimes,
        firstAvailableAppointmentTime,
        preselectedFirstAvailableDate,
        fetchAvailableTimesStatus,
        noAvailableSlotsInCurrentMonth,
        handleSetSelectedAppointmentDate,
    ])

    const handleSetSelectedAppointmentTime = (appointmentTime: string) => () => {
        setErrorMessage('')
        dispatch({
            type: 'SET_SELECTED_APPOINTMENT_TIME',
            appointmentTime,
        })
    }

    const handleChangeProvider = async (value?: ValueType) => {
        await dispatch({
            type: 'UPDATE_PATIENT_DETAILS',
            fieldName: 'provider',
            value,
        })

        runFetchAvailableTimes(
            fetchAvailableTimes(dispatch, {
                locationId: selectedLocationId,
                procedureId,
                providerId: value,
                from: currentFrom,
                to: dayjs(currentFrom).endOf('month').format(CALENDAR_DATE_FORMAT),
                durationInSeconds: treatmentLength && minutesToSeconds(treatmentLength),
            })
        )
    }

    const fetchAvailableTimesWithNextMonth = async (from: string, procedureId: string) => {
        const endOfTheNextMonth = dayjs(from).add(1, 'month').endOf('month').format(CALENDAR_DATE_FORMAT)

        runFetchAvailableTimes(
            fetchAvailableTimes(dispatch, {
                locationId: selectedLocationId,
                procedureId,
                providerId: patientDetails.provider.value,
                from,
                to: endOfTheNextMonth,
                durationInSeconds: treatmentLength && minutesToSeconds(treatmentLength),
            })
        )
    }

    const validateSelectedTime = async () => {
        setErrorMessage('')
        await runFetchAvailableTimes(
            fetchAvailableTimes(dispatch, {
                locationId: selectedLocationId,
                procedureId,
                providerId: patientDetails.provider.value,
                from: selectedAppointmentDateRangeFrom,
                to: selectedAppointmentDateRangeTo,
                durationInSeconds: treatmentLength && minutesToSeconds(treatmentLength),
            })
        )
        setIsFormSubmitting(true)
    }

    useEffect(() => {
        if (isFormSubmitting && fetchAvailableTimesStatus !== 'pending') {
            const isValid = Boolean(fetchedAvailableTimes.find((at: string) => at === stateAppointmentTime))
            if (isValid) {
                dispatch({
                    type: 'CHANGE_STEP',
                    bookingStep: BookingStep.CONFIRM,
                })
            } else {
                dispatch({
                    type: 'SET_SELECTED_APPOINTMENT_TIME',
                    appointmentTime: '',
                })
                setErrorMessage(
                    'The appointment you selected is no longer available. Please choose another appointment.'
                )
            }
            setIsFormSubmitting(false)
        }
    }, [isFormSubmitting, fetchAvailableTimesStatus, fetchedAvailableTimes, stateAppointmentTime, dispatch])

    const handleSetSelectedAppointmentsDateRange = async (from: string, to: string) => {
        setErrorMessage('')
        setNoAvailableSlotsInCurrentMonth(false)
        setCurrentFrom(from)

        await dispatch({
            type: 'SET_SELECTED_APPOINTMENT_DATE_RANGE',
            from,
            to,
        })

        runFetchAvailableTimes(
            fetchAvailableTimes(dispatch, {
                locationId: selectedLocationId,
                procedureId,
                providerId: patientDetails.provider.value,
                from,
                to,
                durationInSeconds: treatmentLength && minutesToSeconds(treatmentLength),
            })
        )
    }

    useEffect(() => {
        if (fetchAvailableTimesStatus === 'resolved' && availableTimes.length === 0) {
            if (isFirstFetchAvailableTimes && currentFrom) {
                fetchAvailableTimesWithNextMonth(currentFrom, procedureId)
                setIsFirstFetchAvailableTimes(false)
            }
            if (!isFirstFetchAvailableTimes) {
                setNoAvailableSlotsInCurrentMonth(true)
            }
        }
    }, [fetchAvailableTimesStatus, isFirstFetchAvailableTimes, procedureId, currentFrom])

    const setNextAvailableAppointment = (appointmentTime: string) => {
        setNextAvailableAppointmentShown(true)

        dispatch({
            type: 'SET_SELECTED_APPOINTMENT_DATE',
            appointmentDate: timeStringToDateString(appointmentTime),
        })

        dispatch({
            type: 'SET_SELECTED_APPOINTMENT_TIME',
            appointmentTime,
        })
    }

    const isOnSameDay = (day1: string, day2: string) => {
        return dayjs(day1).format(CALENDAR_DATE_FORMAT) === dayjs(day2).format(CALENDAR_DATE_FORMAT)
    }

    const getSelectedDateTimes = () => {
        return selectedAppointmentAvailableTimes.filter((time) =>
            time.includes(dayjs(selectedAppointmentDate).format(CALENDAR_DATE_FORMAT))
        )
    }

    const separateAvailableTimesByPartOfTheDay = (times: string[]) => {
        const separatedTimes: SeparatedTimes = {
            morning: [],
            afternoon: [],
            evening: [],
        }
        times.forEach((time) => {
            const datetimeWithoutOffset = removeDatetimeOffset(time)
            const hour = dayjs(datetimeWithoutOffset).get('hour')

            if (hour >= 0 && hour < 12) {
                separatedTimes.morning.push(time)
            }
            if (hour >= 12 && hour < 17) {
                separatedTimes.afternoon.push(time)
            }
            if (hour >= 17 && hour <= 23) {
                separatedTimes.evening.push(time)
            }
        })

        return separatedTimes
    }

    const renderTimeTilesSegment = (times: string[], partOfTheDay: string) => {
        return (
            <div>
                <div className={style.partOfDay}>{partOfTheDay}</div>
                <div className={style.tileBlock}>
                    {times.map((time) => {
                        return (
                            <TimeTile
                                key={time}
                                isSelected={dayjs(time).format(APPOINTMENT_TIME_FORMAT) === selectedAppointmentTime}
                                onClick={handleSetSelectedAppointmentTime(time)}
                                time={time}
                            />
                        )
                    })}
                </div>
            </div>
        )
    }

    const renderTimeTiles = (times: string[]) => {
        const selectedDateTimes = getSelectedDateTimes()
        const separatedTimes = separateAvailableTimesByPartOfTheDay(selectedDateTimes)

        if (times.length === 0 && !selectedDateTimes) {
            return (
                <div className={style.warningMessage}>
                    <div className={style.icon}>warning</div>
                    Sorry, we do not have any appointments available on this month. Please select another month.
                </div>
            )
        }

        return (
            <div>
                {separatedTimes.morning.length > 0 && renderTimeTilesSegment(separatedTimes.morning, 'Morning')}
                {separatedTimes.afternoon.length > 0 && renderTimeTilesSegment(separatedTimes.afternoon, 'Afternoon')}
                {separatedTimes.evening.length > 0 && renderTimeTilesSegment(separatedTimes.evening, 'Evening')}
            </div>
        )
    }

    const nextAvailableAppointment = (selectedDateText: string, nextAvailableAppointmentTimeWithoutOffset: string) => {
        return (
            <Fragment>
                <div className={style.nextAppLarge}>
                    <div className={style.nextApp}>Next Available Appointment</div>
                    <div
                        className={style.nextAppLink}
                        onClick={() => setNextAvailableAppointment(firstAvailableAppointmentTime)}
                    >{`${dayjs(firstAvailableAppointmentTime).format(APPOINTMENT_DATE_FORMAT)}@${dayjs(
                        nextAvailableAppointmentTimeWithoutOffset
                    ).format(APPOINTMENT_TIME_FORMAT)}`}</div>
                </div>
                <div className={style.selectedDateSmall}>{selectedDateText}</div>
            </Fragment>
        )
    }

    const renderTimeSelectorHeader = () => {
        if (selectedAppointmentDate) {
            const selectedDateText = dayjs(selectedAppointmentDate).format(APPOINTMENT_DATE_FORMAT)

            if (firstAvailableAppointmentTime && !nextAvailableAppointmentShown && !selectedAppointmentTime) {
                const nextAvailableAppointmentTimeWithoutOffset = removeDatetimeOffset(firstAvailableAppointmentTime)
                const shouldShowNextAvailableAppointmentLink = isOnSameDay(
                    selectedAppointmentDate,
                    firstAvailableAppointmentTime
                )
                if (shouldShowNextAvailableAppointmentLink) {
                    return nextAvailableAppointment(selectedDateText, nextAvailableAppointmentTimeWithoutOffset)
                }
            }
            return selectedDateText
        }

        if (noAvailableSlotsInCurrentMonth && firstAvailableAppointmentTime) {
            const selectedDateText = dayjs(firstAvailableAppointmentTime).format(APPOINTMENT_DATE_FORMAT)
            const nextAvailableAppointmentTimeWithoutOffset = removeDatetimeOffset(firstAvailableAppointmentTime)
            return nextAvailableAppointment(selectedDateText, nextAvailableAppointmentTimeWithoutOffset)
        }
        return 'Select Date'
    }

    const backStep = showAdditionalDetailsForm ? BookingStep.ADDITIONAL_INFO : BookingStep.DETAILS

    return (
        <section class={style.dateTimeSelectorContainer}>
            <StepBody>
                {Boolean(errorMessage) && <ErrorMessage>{errorMessage}</ErrorMessage>}
                <div style={Boolean(errorMessage) ? { paddingTop: '40px' } : {}} class={style.dateTimeSelector}>
                    <div class={style.calendarWrapper}>
                        {enabledProviderSelection && (
                            <div class={style.providerWrapper}>
                                <CustomField
                                    customFieldType={CustomFieldType.SELECT}
                                    value={patientDetails.provider.value}
                                    required={patientDetails.provider.isRequired}
                                    error={patientDetails.provider.error}
                                    disabled={!providers.length || !patientDetails.reasonOfAppointment.isValid}
                                    onChange={(value?: ValueType) => handleChangeProvider(value as string)}
                                    placeholder="Any Provider"
                                >
                                    <option selected={patientDetails.provider.value === ''} value="">
                                        Any Provider
                                    </option>
                                    {providers.map((provider) => (
                                        <option key={provider.id} value={provider.id}>
                                            {[
                                                provider.suffix,
                                                provider.first_name,
                                                provider.middle_initial,
                                                provider.last_name,
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                        </option>
                                    ))}
                                </CustomField>
                            </div>
                        )}

                        <Calendar
                            selectedDate={selectedAppointmentDate}
                            onSetDate={handleSetSelectedAppointmentDate}
                            onSetDateRange={handleSetSelectedAppointmentsDateRange}
                            setNextAvailableAppointment={setNextAvailableAppointment}
                            daysWithAvailableAppointmentSlots={daysWithAvailableAppointmentSlots}
                            nextAvailableAppointmentShown={nextAvailableAppointmentShown}
                            canShowNoAvailableAppointmentsMsg={
                                fetchAvailableTimesStatus === 'resolved' &&
                                availableTimes.length === 0 &&
                                !isFirstFetchAvailableTimes
                            }
                            loading={loader}
                            loaderMessage={
                                isFormSubmitting
                                    ? 'Checking if appointment is available'
                                    : 'Looking for available appointments'
                            }
                        />
                    </div>
                    <div class={style.calendarTimeSplitter}></div>
                    <div
                        class={cx({
                            [style.timeSelectorWrapper]: true,
                            [style.hidden]: Boolean(!selectedAppointmentDate),
                        })}
                    >
                        <div className={style.timeSelector}>
                            <div className={style.header}>{renderTimeSelectorHeader()}</div>
                            <div className={style.subheader}>(All appointments are shown in {shortTimezone})</div>
                            {selectedAppointmentDate && renderTimeTiles(availableTimes)}
                        </div>
                    </div>
                </div>
            </StepBody>
            <SelfSchedulerFooter
                backStep={backStep}
                nextStep={BookingStep.CONFIRM}
                onNext={validateSelectedTime}
                isNextDisabled={isFormSubmitting || !selectedAppointmentTime}
            />
        </section>
    )
}

export default DateTimeSelector
